import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Typography,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import LoadingView from './LoadingView';

export interface Column {
  field: string;
  // 文字列のほか、複数情報を1列へまとめた際の見出し配置に JSX も渡せる
  headerName: React.ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  renderCell?: (row: any) => React.ReactNode;
  valueFormatter?: (value: any) => string;
  sticky?: boolean;
}

interface StandardDataTableProps {
  rows: any[];
  columns: Column[];
  loading?: boolean;
  loadingMessage?: string;
  totalCount: number;
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  onSortChange: (field: string) => void;
  hidePagination?: boolean;
  // スクロール位置の保存・復元のために TableContainer の ref を外へ露出する
  scrollContainerRef?: React.Ref<HTMLDivElement>;
}

// size="small" の TableCell の左右の余白の合計 (MUI 既定の padding 6px 16px)
const CELL_PADDING_X = 32;

const StandardDataTable: React.FC<StandardDataTableProps> = ({
  rows,
  columns,
  loading,
  loadingMessage = "データを取得中",
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  hidePagination = false,
  scrollContainerRef,
}) => {
  const displayColumns = columns;

  // 先頭から連続する sticky 列の left オフセットを積み上げる (複数列固定に対応)。
  // width 未指定・文字列指定の列は幅を確定できないため、そこで固定を打ち切る。
  // CssBaseline を使っていないため TableCell は content-box で、描画幅は width に左右の余白が足される。
  // 余白を足さないと 2 列目以降が横スクロールで固定され始めたときに左へずれる
  const stickyOffsets = React.useMemo(() => {
    const offsets: Record<string, number> = {};
    let left = 0;
    for (const column of displayColumns) {
      if (!column.sticky) break;
      offsets[column.field] = left;
      if (typeof column.width !== 'number') break;
      left += column.width + CELL_PADDING_X;
    }
    return offsets;
  }, [displayColumns]);

  // 固定領域の右端 (スクロールする列との境界) に区切り線を入れる
  const lastStickyField = Object.keys(stickyOffsets).slice(-1)[0];

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoadingView message={loadingMessage} size={50} />
          </Box>
        )}
        <TableContainer ref={scrollContainerRef} sx={{ flexGrow: 1, overflowX: 'auto' }}>
          <Table stickyHeader size="small" aria-label="sticky table">
            <TableHead>
              <TableRow>
                {displayColumns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align}
                    sx={{
                      width: column.width,
                      // 固定列は他列の内容で押し潰されないよう幅を確定させる
                      ...(stickyOffsets[column.field] !== undefined && typeof column.width === 'number'
                        ? { minWidth: column.width, maxWidth: column.width }
                        : {}),
                      backgroundColor: '#f5f5f5',
                      color: 'grey.500',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      // 列固定のためのスタイル
                      ...(stickyOffsets[column.field] !== undefined && {
                        position: 'sticky',
                        left: stickyOffsets[column.field],
                        backgroundColor: '#f5f5f5',
                        ...(column.field === lastStickyField && { borderRight: '1px solid #e2e8f0' }),
                      }),
                      // ヘッダー固定(stickyHeader)との組み合わせ
                      zIndex: stickyOffsets[column.field] !== undefined ? 11 : 1,
                    }}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row.project_id || row.id || index}
                  sx={{
                    '&:hover .MuiTableCell-root': {
                      backgroundColor: '#f5f5f5 !important',
                    }
                  }}
                >
                  {displayColumns.map((column) => {
                    const value = row[column.field];
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: column.width || 'none',
                          // 固定列は他列の内容で押し潰されないよう幅を確定させる
                          ...(stickyOffsets[column.field] !== undefined && typeof column.width === 'number'
                            ? { minWidth: column.width, width: column.width }
                            : {}),
                          // 列固定のためのスタイル (背景は行のホバー色に追随しないため白で塗る)
                          ...(stickyOffsets[column.field] !== undefined && {
                            position: 'sticky',
                            left: stickyOffsets[column.field],
                            zIndex: 1,
                            backgroundColor: '#fff',
                            ...(column.field === lastStickyField && { borderRight: '1px solid #e2e8f0' }),
                          })
                        }}
                      >
                        {column.renderCell
                          ? column.renderCell(row)
                          : column.valueFormatter
                            ? column.valueFormatter(value)
                            : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={displayColumns.length} align="center" sx={{ py: 3 }}>
                    該当データがありません。
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {!hidePagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1.5,
            py: 0.5,
            minHeight: 36,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Select
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange(Number(e.target.value));
                // 件数変更で現在ページが範囲外になるのを防ぐため先頭へ戻す
                onPageChange(0);
              }}
              size="small"
              sx={{
                height: 26,
                fontSize: '0.8125rem',
                color: 'text.secondary',
                '& .MuiSelect-select': { py: 0.25, pl: 1, pr: '24px !important' },
              }}
            >
              <MenuItem value={50}>50件</MenuItem>
              <MenuItem value={100}>100件</MenuItem>
            </Select>
            <Typography variant="caption" color="text.disabled">
              {totalCount === 0 ? (
                '0 件'
              ) : (
                <>
                  <strong style={{ color: 'rgba(0,0,0,0.7)' }}>
                    {(page * pageSize + 1).toLocaleString()} 〜 {(page * pageSize + rows.length).toLocaleString()}
                  </strong>{' '}
                  件 / {totalCount.toLocaleString()} 件
                </>
              )}
            </Typography>
          </Box>
          <Pagination
            count={Math.max(1, Math.ceil(totalCount / pageSize))}
            page={page + 1}
            onChange={(_, p) => onPageChange(p - 1)}
            shape="rounded"
            size="small"
            siblingCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 600,
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                bgcolor: blueGrey[600],
                color: '#ffffff',
                '&:hover': { bgcolor: blueGrey[600] },
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

// props（rows / columns / ハンドラ等）の参照が変わらない限り再描画をスキップする。
// 呼び出し側は rows・columns を useMemo し、ハンドラを useCallback またはモジュール定数で
// 安定参照にすると memo が効く（inline 生成のままでも従来どおり動作し、害はない）。
export default React.memo(StandardDataTable);
